import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { DEMO_GUEST, DemoPresence, demoBots as demoSwitch } from '../../collaboration/demoBots';
import { closeItemPanels } from '../../collaboration/rt/liveSession';
import { DemoShare } from '../../collaboration/share/demoShare';
import type { ShareState } from '../../collaboration/share/source';
import { store, type EditScope } from '../../state/store';
import { agentCursor } from '../../agent/cursor';

const noShare = () => () => {};

/**
 * The demo bots of a project of this browser (docs/collaboration.md «Demo bots»), their Share source, and «View as a
 * guest»: you become their guest `DEMO_GUEST` with the guest's rights while the bots stay. `state` is the demo's Share
 * state while it is the Share shown (no live room), else null. Only the user's own choice brings the scripted bots and
 * the people; a chat run that borrows the presence brings just its subagents at work, and `room` is null then: the
 * project keeps the chat and the Share dialog of a project nobody shares; the header lists the subagents as participants.
 */
export function useDemoRoom({ on, shared, live, edit, projectId }: { on: boolean; shared: boolean; live: boolean; edit: EditScope; projectId: string }) {
  const [guestOf, setGuestOf] = useState<string | null>(null);
  const asGuest = guestOf === projectId;
  const ownProject = edit === 'all' || asGuest;
  const chosen = useSyncExternalStore(demoSwitch.subscribe, demoSwitch.chosenNow);
  const demo = useMemo(() => on && !shared && ownProject ? new DemoPresence({ store, people: chosen, scripted: chosen }) : null, [on, shared, ownProject, chosen]);
  useEffect(() => {
    if (!demo) return;
    demo.start(); demoSwitch.attach(demo);
    const show = () => demo.showAgents(agentCursor.all());
    show();
    const unsubscribe = agentCursor.subscribe(show);
    return () => { unsubscribe(); demo.stop(); if (demoSwitch.current() === demo) demoSwitch.attach(null); };
  }, [demo]);
  const room = demo?.scripted ? demo : null;
  const guest = asGuest && !!room;
  const demoShare = useMemo(() => new DemoShare(room, guest ? 'guest' : 'owner'), [room, guest]);
  const shown = room && !live ? demoShare : null;
  const state: ShareState | null = useSyncExternalStore(shown?.subscribe ?? noShare, () => shown?.get() ?? null);
  const guestRole = guest ? state?.myRole ?? null : null;
  const viewAsGuest = useCallback((as: boolean) => {
    // Back as the owner, the edit is given back before the guest view ends, so the bots' room is never let go meanwhile.
    if (!as && store.get().edit !== 'all') store.set({ edit: 'all' });
    setGuestOf(as ? store.get().projectId : null);
  }, []);
  useEffect(() => {
    if (!guestRole) return;
    closeItemPanels(store, { edit: guestRole === 'viewer' ? 'none' : 'all' });
    demo?.viewAs(DEMO_GUEST);
    return () => demo?.viewAs(null);
  }, [guestRole, demo]);
  // The bots sent away while you are their guest: you are the owner again.
  useEffect(() => { if (asGuest && !room) viewAsGuest(false); }, [asGuest, room, viewAsGuest]);
  useEffect(() => { setGuestOf(current => current === projectId ? current : null); }, [projectId]);
  const canViewAsGuest = !!state && (guest || state.people.some(person => person.id === DEMO_GUEST));
  return { demo, room, demoShare, ownProject, guest, state, viewAsGuest, canViewAsGuest };
}
